import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PLAN_STORAGE_KEY } from '../utils/subscriptionPlan';
import './SubscriptionPayment.css';

const PLANS = [
  {
    id: 'monthly',
    title: '1 Month',
    badge: 'Fast start',
    price: 100,
    originalPrice: 200,
    period: '/month',
    features: [
      'Everything in 1 Month plan',
      'Unlimited mail and attachments',
      'Priority Kannada + English support',
      'Daily cloud backup for 30 days',
    ],
    displayOnly: true,
  },
  {
    id: 'sixMonth',
    title: '6 Months',
    price: 600,
    originalPrice: 1200,
    period: '/6 months',
    features: [
      'Everything in 6 Month plan',
      'Advanced filters and smart labels',
      'Business profile badge and lead tools',
    ],
    cta: 'Claim Launch Offer',
    ctaVariant: 'primary',
    featured: true,
    launchFree: true,
  },
  {
    id: 'yearly',
    title: '1 Year',
    badge: 'Best savings',
    price: 12000,
    originalPrice: 24000,
    period: '/year',
    features: [
      'Everything in 12 Month plan',
      'Advanced admin controls + audit history',
      'Premium onboarding and growth playbook',
    ],
    cta: 'Upgrade to Pro',
    ctaVariant: 'secondary',
  },
];

const formatRs = (amount) => `Rs ${Number(amount).toLocaleString('en-IN')}`;

const SubscriptionPayment = ({ currentUser }) => {
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState(() => {
    return localStorage.getItem(PLAN_STORAGE_KEY) || null;
  });
  const [showYearlyPopup, setShowYearlyPopup] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState('');

  const yearlyPlan = PLANS.find((plan) => plan.id === 'yearly');

  const persistPlan = (planId) => {
    localStorage.setItem(PLAN_STORAGE_KEY, planId);
    setCurrentPlan(planId);
    window.dispatchEvent(new Event('atozas-subscription-change'));
  };

  const handleActivateSixMonth = () => {
    persistPlan('sixMonth');
    setPaymentMessage('Your 6 Month launch offer is now active — free access enabled.');
  };

  const handlePlanAction = (plan) => {
    if (plan.id === currentPlan) return;

    if (plan.id === 'sixMonth') {
      handleActivateSixMonth();
      return;
    }

    if (plan.id === 'yearly') {
      setShowYearlyPopup(true);
    }
  };

  const handleYearlyPayment = () => {
    persistPlan('yearly');
    setPaymentMessage(`1 Year plan activated for ${currentUser?.email || 'your account'}.`);
    setShowYearlyPopup(false);
    navigate('/inbox');
  };

  const renderPlanButton = (plan) => {
    if (plan.displayOnly) return null;

    const isActive = plan.id === currentPlan;

    if (isActive) {
      return (
        <div className="subscription-plan-active-label" aria-live="polite">
          Current plan
        </div>
      );
    }

    return (
      <button
        type="button"
        className={`subscription-plan-cta subscription-plan-cta--${plan.ctaVariant}`}
        onClick={() => handlePlanAction(plan)}
      >
        {plan.cta}
      </button>
    );
  };

  return (
    <div className="subscription-payment-page">
      <div className="subscription-payment-inner">
        <button
          type="button"
          className="subscription-back-btn"
          onClick={() => navigate('/inbox')}
        >
          ← Back to inbox
        </button>

        <div className="subscription-pricing-hero">
          <p className="subscription-pricing-kicker">TRANSPARENT PRICING</p>
          <div className="subscription-launch-banner">
            <span>LAUNCHING OFFER</span>
            <strong>100% OFF</strong>
            <span>Limited time access for everyone</span>
          </div>
          <h1>Pick a plan that grows with your mail community</h1>
          <p className="subscription-pricing-subtitle">
            Simple monthly options and smart long-term savings for creators, teams, and businesses using ATOZASMail.
          </p>
          {/* <div className="subscription-trust-badges">
            <span>Instant activation</span>
            <span>Secure payments</span>
            <span>Cancel anytime</span>
          </div> */}
        </div>

        {paymentMessage && (
          <p className="subscription-payment-toast" role="status">
            {paymentMessage}
          </p>
        )}

        <div className="subscription-plan-grid">
          {PLANS.map((plan) => {
            const isActive = plan.id === currentPlan;
            return (
              <article
                key={plan.id}
                className={`subscription-plan-card${plan.featured ? ' featured' : ''}${isActive ? ' active-plan' : ''}`}
              >
                <div className="subscription-plan-card-top">
                  <h2>{plan.title}</h2>
                  <div className="subscription-plan-badges">
                    {isActive && (
                      <span className="subscription-plan-badge subscription-plan-badge--active">ACTIVE PLAN</span>
                    )}
                    {plan.id === 'sixMonth' && (
                      <span className="subscription-plan-badge subscription-plan-badge--muted">Most Popular</span>
                    )}
                    {plan.badge && (
                      <span className="subscription-plan-badge subscription-plan-badge--muted">{plan.badge}</span>
                    )}
                  </div>
                </div>

                <div className="subscription-plan-price-block">
                  <div className="subscription-plan-price">{formatRs(plan.price)}</div>
                  <div className="subscription-plan-original">
                    <s>{formatRs(plan.originalPrice)}</s>
                    <span>{plan.period}</span>
                  </div>
                </div>

                <ul className="subscription-plan-features">
                  {plan.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>

                {renderPlanButton(plan)}
              </article>
            );
          })}
        </div>
      </div>

      {showYearlyPopup && (
        <div className="subscription-popup-overlay" onClick={() => setShowYearlyPopup(false)}>
          <div className="subscription-popup" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="yearly-upgrade-title">
            <button
              type="button"
              className="subscription-popup-close"
              onClick={() => setShowYearlyPopup(false)}
              aria-label="Close"
            >
              ×
            </button>
            <p className="subscription-popup-kicker">Upgrade your plan</p>
            <h2 id="yearly-upgrade-title">Move to Yearly Pro</h2>
            <p className="subscription-popup-copy">
              Unlock the best long-term value for <strong>{currentUser?.email}</strong>.
            </p>
            <div className="subscription-popup-price">
              <span>{formatRs(yearlyPlan.price)}</span>
              <small>{yearlyPlan.period}</small>
            </div>
            <ul className="subscription-popup-features">
              {yearlyPlan.features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
            <button type="button" className="subscription-popup-pay-btn" onClick={handleYearlyPayment}>
              Pay {formatRs(yearlyPlan.price)} now
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default SubscriptionPayment;
